import React, { useMemo } from "react";
import LineChart from "./LineChart";

const truncateLabel = (value, maxLen = 12) => {
  const str = value == null ? "" : String(value);
  if (!maxLen || str.length <= maxLen) return str;
  return `${str.slice(0, Math.max(0, maxLen - 1))}…`;
};

const buildAreaDataset = (dataset) => {
  return {
    ...dataset,
    fill: typeof dataset?.fill === "boolean" ? dataset.fill : true,
    tension: typeof dataset?.tension === "number" ? dataset.tension : 0.35,
    pointRadius: typeof dataset?.pointRadius === "number" ? dataset.pointRadius : 0,
    pointHoverRadius:
      typeof dataset?.pointHoverRadius === "number" ? dataset.pointHoverRadius : 4,
    pointHoverBorderWidth:
      typeof dataset?.pointHoverBorderWidth === "number" ? dataset.pointHoverBorderWidth : 2,
    pointHoverBorderColor: dataset?.pointHoverBorderColor || "rgba(255,255,255,1)",
    pointHoverBackgroundColor:
      dataset?.pointHoverBackgroundColor || dataset?.borderColor || dataset?.backgroundColor,
    borderWidth: typeof dataset?.borderWidth === "number" ? dataset.borderWidth : 3,
  };
};

const resolveDatasetColor = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw[0] || null;
  if (typeof raw === "string") return raw;
  return null;
};

const AreaChart = ({ data, options }) => {
  const chartData = useMemo(() => {
    const datasets = Array.isArray(data?.datasets) ? data.datasets : [];
    return {
      ...(data || {}),
      datasets: datasets.map((d) => buildAreaDataset(d)),
    };
  }, [data]);

  const verticalHoverLinePlugin = useMemo(
    () => ({
      id: "verticalHoverLine",
      afterDraw: (chart) => {
        const tooltip = chart?.tooltip;
        if (!tooltip || !tooltip._active || tooltip._active.length === 0) return;
        const activePoint = tooltip._active[0];
        if (!activePoint || !activePoint.element) return;

        const ctx = chart.ctx;
        const x = activePoint.element.x;
        const topY = chart.scales?.y?.top;
        const bottomY = chart.scales?.y?.bottom;
        if (typeof topY !== "number" || typeof bottomY !== "number") return;

        const ds0 = chart?.data?.datasets?.[0] || null;
        const hoverColor =
          resolveDatasetColor(ds0?.borderColor) ||
          resolveDatasetColor(ds0?.backgroundColor);
        if (!hoverColor) return;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = hoverColor;
        ctx.stroke();
        ctx.restore();
      },
    }),
    [],
  );

  const mergedOptions = useMemo(() => {
    const userScales = options?.scales || {};
    const userX = userScales?.x || {};
    const userXTicks = userX?.ticks || {};
    const maxLabelLength =
      typeof userXTicks.maxLabelLength === "number" ? userXTicks.maxLabelLength : 12;

    const ds0 = chartData?.datasets?.[0] || null;
    const accentColor =
      resolveDatasetColor(ds0?.borderColor) || resolveDatasetColor(ds0?.backgroundColor);

    const baseTooltip = {
      enabled: true,
      displayColors: false,
      backgroundColor: "rgba(255,255,255,0.98)",
      titleColor: "rgba(17, 24, 39, 1)",
      bodyColor: "rgba(17, 24, 39, 1)",
      borderColor: accentColor || "rgba(148, 163, 184, 0.35)",
      borderWidth: 1,
      padding: 10,
      cornerRadius: 10,
      callbacks: {
        title: (items) => (items && items[0] ? String(items[0].label || "") : ""),
        label: (item) => {
          const v = item?.parsed?.y;
          const value = Number.isFinite(Number(v)) ? Number(v) : 0;
          return `${value}`;
        },
      },
    };

    return {
      responsive: true,
      interaction: { mode: "index", intersect: false },
      ...options,
      plugins: {
        legend: { display: false },
        tooltip: { ...baseTooltip, ...(options?.plugins?.tooltip || {}) },
        ...(options?.plugins || {}),
      },
      scales: {
        x: {
          grid: {
            color: "rgba(148, 163, 184, 0.25)",
            borderDash: [4, 4],
            drawBorder: false,
          },
          ticks: {
            color: "rgba(107, 114, 128, 1)",
            minRotation: 0,
            maxRotation: 0,
            autoSkip: true,
            ...userXTicks,
            callback: function (value, index, ticks) {
              const raw =
                this && typeof this.getLabelForValue === "function"
                  ? this.getLabelForValue(value)
                  : value;
              if (typeof userXTicks.callback === "function") {
                return userXTicks.callback.call(this, value, index, ticks);
              }
              if (!maxLabelLength || maxLabelLength <= 0) return String(raw ?? "");
              return truncateLabel(raw, maxLabelLength);
            },
          },
          ...userX,
        },
        y: {
          beginAtZero: true,
          grid: {
            color: "rgba(148, 163, 184, 0.25)",
            borderDash: [4, 4],
            drawBorder: false,
          },
          ticks: { color: "rgba(107, 114, 128, 1)", stepSize: 6 },
          ...(userScales?.y || {}),
        },
        ...userScales,
      },
    };
  }, [chartData, options]);

  return <LineChart data={chartData} options={mergedOptions} plugins={[verticalHoverLinePlugin]} />;
};

export default AreaChart;
